import React, { useState, useEffect } from "react";
import { Alert, Spinner } from "react-bootstrap";
import CustomDataGrid from "../components/CustomDataGrid";
import EntityDetails from "../components/EntityDetails";
import PageHelmet from "../components/PageHelmet";
import { fetchAdminActivityLogs } from "../utils/api";
import "../styles/AdminActivityLogs.css";
import {
    generateGridColumns,
    generateGridRows,
    activityLogsGridColumns,
    activityLogsOptionalColumns,
    activityLogsRowMapping,
    activityLogsMapping,
    generateFormData
} from "../utils/PageUtils";

const AdminActivityLogs = ({ userRole, bearerToken }) => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedLog, setSelectedLog] = useState(null);
    const [selectedRowId, setSelectedRowId] = useState(null);

    useEffect(() => {
        const getLogs = async () => {
            if (!bearerToken) return;
            setLoading(true);
            try {
                const response = await fetchAdminActivityLogs(bearerToken);
                if (response.status === 200) {
                    const data = await response.data.records;
                    const sortedLogs = data.sort((a, b) => new Date(b.created) - new Date(a.created));
                    setLogs(sortedLogs);
                } else if (response.status === 404) {
                    setError(response.data?.message || "No activity logs found");
                }
                else {
                    setError(response.data?.message || "Something went wrong while attempting to display data");
                }
            } catch (err) {
                setError("An error occurred while fetching activity logs.");
            } finally {
                setLoading(false);
            }
        };
        getLogs();
    }, []);

    const handleRowClick = (e) => {
        if (selectedRowId === e.id) {
            setSelectedRowId(null);
            setSelectedLog(null);
        } else {
            setSelectedRowId(e.id);
            setSelectedLog(activityLogsRows.find(log => log.id === e.id));
        }
    };

    const activityLogsColumns = generateGridColumns(activityLogsGridColumns, "action", undefined, undefined, activityLogsOptionalColumns);
    const activityLogsRows = generateGridRows(logs, activityLogsRowMapping);

    const activityLogFormData = selectedLog
        ? generateFormData(selectedLog, activityLogsMapping)
        : null;

    return userRole === "admin" ? (
        <div>
            <PageHelmet
                title="Activity Logs"
                description="View administrative activity logs."
                url={`${process.env.REACT_APP_REDIRECT_URI}/activity_logs`}
            />
            <div className="user-container activity-logs-container">
                <h1 className="user-heading">Activity Logs</h1>
                {error && <Alert variant="danger">{error}</Alert>}
                {loading && (
                    <div className="text-center p-5">
                        <Spinner animation="border" variant="primary" >
                            <span className="visually-hidden">Please wait...</span>
                        </Spinner>
                    </div>
                )}
                {!error && (
                    <div className="create-main-container custom-grid-container">
                        <div className={`create-grid-form ${selectedLog ? "grid-to-left" : "grid-centered"}`}>
                            <div className="create-grid-container">
                                <h3 className="heading-color">Activity Logs</h3>
                                <CustomDataGrid
                                    rows={activityLogsRows}
                                    columns={activityLogsColumns}
                                    totalRows={logs.length}
                                    onRowClick={handleRowClick}
                                    selectedRowId={selectedRowId}
                                />
                            </div>
                            {selectedLog && (
                                <div className="create-form-container">
                                    <h3 className="heading-color">Log Details</h3>
                                    <EntityDetails inputs={activityLogFormData} width={"124px"} />
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div >
    ) : (
        <div className="user-container" style={{ marginTop: "100px" }}>
            <Alert variant="danger" className="text-center">
                You do not have access to this page.
            </Alert>
        </div>
    );
};

export default AdminActivityLogs;